import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { extrairMensagemErro, salvarEmExcel } from '~/Utils/Helpers';
import {
  consultarHistoricoImoveis,
  listarGruposEmpresaHistoricoImoveis,
  listarGruposGEHistoricoImoveis,
  listarEmpreendimentosHistoricoImoveis,
  listarCorrecoesHistoricoImoveis,
} from '~/Services/Service';
import FiltrosHistoricoImoveis from './FiltrosHistoricoImoveis';
import TabelaHistoricoImoveis from './TabelaHistoricoImoveis';

const HistoricoImoveis = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [gruposGe, setGruposGe] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [carregandoLookups, setCarregandoLookups] = useState(true);
  const [carregandoEmpreendimentos, setCarregandoEmpreendimentos] = useState(
    false
  );
  const [carregandoRelatorio, setCarregandoRelatorio] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [mostrarCorrecoes, setMostrarCorrecoes] = useState(false);

  useEffect(() => {
    let ativo = true;
    const carregarLookupsIniciais = async () => {
      try {
        const [respGes, respGruposGe] = await Promise.all([
          listarGruposEmpresaHistoricoImoveis(),
          listarGruposGEHistoricoImoveis(),
        ]);
        if (!ativo) return;
        setGruposEmpresa(respGes || []);
        setGruposGe(respGruposGe || []);
      } catch (err) {
        if (!ativo) return;
        toast.error(extrairMensagemErro(err));
      } finally {
        if (ativo) setCarregandoLookups(false);
      }
    };
    carregarLookupsIniciais();
    return () => {
      ativo = false;
    };
  }, []);

  // RN-02: recarrega empreendimentos quando GE ou Grupo de GE muda.
  const handleGeChange = useCallback((codigoGrupoEmpresa, codigoGrupoGE) => {
    if (
      (codigoGrupoEmpresa === null || codigoGrupoEmpresa === undefined) &&
      (codigoGrupoGE === null || codigoGrupoGE === undefined)
    ) {
      setEmpreendimentos([]);
      return;
    }
    setCarregandoEmpreendimentos(true);
    listarEmpreendimentosHistoricoImoveis(codigoGrupoEmpresa, codigoGrupoGE)
      .then((resp) => {
        setEmpreendimentos(resp || []);
      })
      .catch((err) => {
        setEmpreendimentos([]);
        toast.error(extrairMensagemErro(err));
      })
      .finally(() => {
        setCarregandoEmpreendimentos(false);
      });
  }, []);

  const handleSubmit = useCallback(async (filtros) => {
    setCarregandoRelatorio(true);
    setMostrarCorrecoes(!!filtros.historicoCorrecoes);
    try {
      const resp = await consultarHistoricoImoveis(filtros);
      const lista = Array.isArray(resp) ? resp : [];
      setResultado(lista);
      if (lista.length === 0) {
        toast.info('Nenhuma informação encontrada.');
      }
    } catch (err) {
      setResultado(null);
      toast.error(extrairMensagemErro(err));
    } finally {
      setCarregandoRelatorio(false);
    }
  }, []);

  const handleCancelar = useCallback(() => {
    setResultado(null);
    setEmpreendimentos([]);
    setMostrarCorrecoes(false);
  }, []);

  // Loader de correções de um boleto (expansão da linha no grid).
  const handleExpandirBoleto = useCallback(
    (boleto) => listarCorrecoesHistoricoImoveis(boleto),
    []
  );

  // Exporta TODOS os registros do resultado (não só a página atual). O xlsx é gerado
  // no backend (ExportadorController); o front apenas envia a lista carregada.
  const exportarExcel = useCallback(async () => {
    if (!resultado || resultado.length === 0) return;
    try {
      await salvarEmExcel(resultado, '/exportador/excel/historico-imoveis');
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    }
  }, [resultado]);

  return (
    <Container fluid className="mb-4 mt-3">
      <Header title="Histórico de Imóveis" />
      {carregandoLookups ? (
        <Loading />
      ) : (
        <>
          <FiltrosHistoricoImoveis
            gruposEmpresa={gruposEmpresa}
            gruposGe={gruposGe}
            empreendimentos={empreendimentos}
            carregandoEmpreendimentos={carregandoEmpreendimentos}
            onGeChange={handleGeChange}
            onSubmit={handleSubmit}
            onCancelar={handleCancelar}
            submitting={carregandoRelatorio}
          />
          {carregandoRelatorio ? (
            <Loading />
          ) : (
            resultado && (
              <TabelaHistoricoImoveis
                itens={resultado}
                mostrarCorrecoes={mostrarCorrecoes}
                onExpandirBoleto={handleExpandirBoleto}
                onExportar={exportarExcel}
              />
            )
          )}
        </>
      )}
    </Container>
  );
};

export default HistoricoImoveis;
